import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid } from '@material-ui/core'
import RoundedModal from '../../../molecules/RoundedModal'
import SydButton from '../../../commonDesign/Button'
import SydLabel from '../../../commonDesign/SydLabel'
import SydInput from '../../../commonDesign/SydInput'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import { connections } from '../../../../api/integrations'
import { decipherError } from '../../../../utils/decipherError'
import OAuthConnectButton from './OAuthConnectButton'

const AuthDialog = React.forwardRef(function AuthDialog ({ onComplete }, ref) {
  const [dialogState, setDialogState] = useState({ open: false })
  const [formValues, setFormValues] = useState({})
  const [error, setError] = useState(null)
  const { mutateAsync: authorize, isLoading } = connections.useAuthorizeUserConnectionToken()

  const close = useCallback(() => {
    setDialogState({ open: false })
    setFormValues({})
    setError(null)
  }, [])

  useImperativeHandle(ref, () => ({
    open: (domain, authInputs, options = {}) => {
      setDialogState({ open: true, domain, authInputs, domainName: options.domainName })
      setFormValues({})
      setError(null)
    }
  }), [])

  const handleFieldChange = useCallback((fieldKey, value) => {
    setFormValues(prev => ({ ...prev, [fieldKey]: value }))
  }, [])

  const submit = useCallback(async (extraArgs = {}) => {
    try {
      setError(null)
      const domain = dialogState.domain
      const result = await authorize({
        domain,
        authArgs: {
          ...formValues,
          ...extraArgs
        }
      })
      close()
      onComplete({ ...result, domain })
    } catch (err) {
      setError(decipherError(err) || 'Authentication failed')
    }
  }, [authorize, dialogState.domain, formValues, close, onComplete])

  const handleFormSubmit = useCallback((e) => {
    e.preventDefault()
    if (isLoading) return
    const inputs = dialogState.authInputs || {}
    const missing = Object.entries(inputs)
      .filter(([key]) => !formValues[key] || String(formValues[key]).trim() === '')
      .map(([, cfg]) => cfg.label)
    if (missing.length) {
      setError(`Please provide: ${missing.join(', ')}`)
      return
    }
    submit()
  }, [submit, isLoading, dialogState.authInputs, formValues])

  const handleOAuthAuthorized = useCallback(({ code }) => {
    submit({ code }).catch(() => { /* error set in state */ })
  }, [submit])

  const handleOAuthError = useCallback((err) => {
    setError(err?.message || 'Authorization was cancelled')
  }, [])

  const authInputs = dialogState.authInputs || {}
  const isOAuth = authInputs?.$type === 'oauth'
  const displayDomain = dialogState.domainName ||
    (dialogState.domain
      ? dialogState.domain.charAt(0).toUpperCase() + dialogState.domain.slice(1)
      : '')
  const title = `Connect ${displayDomain}`

  return (
    <RoundedModal
      title={title}
      open={dialogState.open}
      onClose={close}
      size='small'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <form onSubmit={handleFormSubmit} noValidate>
            <Grid container spacing={2}>
              {isOAuth ? (
                <>
                  <Grid item xs={12}>
                    <div className='text-size-sm' style={{ color: 'var(--color-text-500)' }}>
                      You'll be redirected to authorize {displayDomain}. After you approve, the window will close automatically.
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <OAuthConnectButton
                      domain={dialogState.domain}
                      authorizationUrl={authInputs.authorizationUrl}
                      clientId={authInputs.clientId}
                      redirectUri={authInputs.redirectUri}
                      scope={authInputs.scope}
                      responseType={authInputs.responseType}
                      label={isLoading ? 'Completing...' : `Authorize ${displayDomain}`}
                      onAuthorized={handleOAuthAuthorized}
                      onError={handleOAuthError}
                      disabled={isLoading}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  {Object.entries(authInputs).map(([key, config]) => (
                    <Grid item xs={12} key={key}>
                      <SydLabel label={config.label} required>
                        <SydInput
                          name={key}
                          type={config.type === 'password' ? 'password' : 'text'}
                          placeholder={config.label}
                          value={formValues[key] || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                        />
                      </SydLabel>
                    </Grid>
                  ))}
                </>
              )}
              {error ? (
                <Grid item xs={12}>
                  <div className='text-size-sm' style={{ color: 'var(--color-danger-500)' }}>{error}</div>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <SydModalActions>
                  <SydButton type='button' variant='outline' onClick={close}>Cancel</SydButton>
                  {!isOAuth ? (
                    <SydButton type='submit' variant='primary' processing={isLoading}>Connect</SydButton>
                  ) : null}
                </SydModalActions>
              </Grid>
            </Grid>
          </form>
        ) : null}
      </div>
    </RoundedModal>
  )
})

AuthDialog.propTypes = {
  onComplete: PropTypes.func
}

AuthDialog.defaultProps = {
  onComplete: () => {}
}

export default AuthDialog
